import * as z from "zod";
import type { SessionState } from "@/convex/session";
import { storageKey } from "@/site";

// Set by this app's own sign-out button, in this tab only. /signed-out
// forgets a reader only when it is present, so a cross-site link to
// /signed-out cannot erase a reader's progress.
export const SIGN_OUT_INTENT_KEY = storageKey("signOutIntent.v1");
// Long enough for the round trip through the broker's sign-out; a mark
// left by a sign-out that never reached /signed-out must not fire later.
export const SIGN_OUT_INTENT_MAX_AGE_MS = 2 * 60 * 1000;

const signOutIntentSchema = z.object({
  lane: z.enum(["reader_session", "user_session"]),
  readerId: z.uuid().nullable(),
});

/** The session that chose to sign out, which /signed-out acts on. */
export type SignOutIntent = z.infer<typeof signOutIntentSchema>;

const storedIntentSchema = signOutIntentSchema.extend({ at: z.number() });

export function signOutIntentOf(state: SessionState): SignOutIntent | null {
  if (state.status !== "ready") return null;
  return { lane: state.session.lane, readerId: state.session.readerId };
}

/**
 * Only a reader_session's sign-out takes its reader off this device. An
 * account's sign-out leaves every reader here as it was: a parent signing
 * out must not erase a child who reads on this device without them.
 */
export function readerToForget(intent: SignOutIntent | null): string | null {
  return intent?.lane === "reader_session" ? intent.readerId : null;
}

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const tabStorage = (): Store | null => {
  try {
    return sessionStorage;
  } catch {
    return null;
  }
};

export function markSignOutIntent(
  intent: SignOutIntent | null,
  now: number = Date.now(),
  storage: Store | null = tabStorage(),
): void {
  if (intent === null) storage?.removeItem(SIGN_OUT_INTENT_KEY);
  else
    storage?.setItem(
      SIGN_OUT_INTENT_KEY,
      JSON.stringify({ ...intent, at: now }),
    );
}

function parseIntent(
  raw: string | null | undefined,
  now: number,
): SignOutIntent | null {
  if (!raw) return null;
  try {
    const parsed = storedIntentSchema.safeParse(JSON.parse(raw));
    if (!parsed.success) return null;
    const { at, lane, readerId } = parsed.data;
    const age = now - at;
    return age >= 0 && age <= SIGN_OUT_INTENT_MAX_AGE_MS
      ? { lane, readerId }
      : null;
  } catch {
    return null;
  }
}

/** The sign-out this tab chose just now, if any; reading it spends it. */
export function takeSignOutIntent(
  now: number = Date.now(),
  storage: Store | null = tabStorage(),
): SignOutIntent | null {
  const intent = parseIntent(storage?.getItem(SIGN_OUT_INTENT_KEY), now);
  storage?.removeItem(SIGN_OUT_INTENT_KEY);
  return intent;
}
